import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, MessageCircle, Linkedin, Instagram } from 'lucide-react';
import { contactInfo, companyBranding } from '../data/mock';

const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const waNumber = contactInfo.whatsapp.replace(/\D/g, '');

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Services Hub', href: '/services' },
    { name: 'All Products & Prices', href: '/all-prices' },
    { name: 'Business Membership', href: '/business-membership' },
    { name: 'Business Tools', href: '/business-tools' },
    { name: 'Knowledge Centre', href: '/blog' },
    { name: 'Trust Centre', href: '/trust-centre' },
    { name: 'Documents', href: '/#documents' },
    { name: 'About', href: '/#about' },
    { name: 'Contact', href: '/#contact' },
  ];

  const serviceGroups = [
    {
      title: 'Start a Business',
      links: [
        { name: 'Private Limited Company', href: '/private-limited-company-registration' },
        { name: 'LLP Registration', href: '/llp-registration' },
        { name: 'Proprietorship Setup', href: '/proprietorship-registration' },
        { name: 'Partnership Firm', href: '/partnership-registration' },
        { name: 'One Person Company', href: '/registration/one-person-company' },
        { name: 'Startup India', href: '/startup-india-registration' },
      ],
    },
    {
      title: 'GST, IP & Licences',
      links: [
        { name: 'GST Registration', href: '/registration/gst-registration' },
        { name: 'GST Rule 14A', href: '/registration/gst-rule-14a' },
        { name: 'LUT Registration', href: '/registration/lut-registration' },
        { name: 'Trademark Application', href: '/trademark-registration' },
        { name: 'Udyam Registration', href: '/registration/udyam-registration' },
        { name: 'IEC Registration', href: '/registration/iec-registration' },
        { name: 'FSSAI Registration', href: '/registration/fssai-registration' },
        { name: 'Shop & Establishment', href: '/registration/shop-establishment' },
        { name: 'Trade Licence', href: '/registration/trade-license' },
        { name: 'Professional Tax', href: '/registration/professional-tax' },
      ],
    },
    {
      title: 'Address & Compliance',
      links: [
        { name: 'Virtual Office', href: '/virtual-office' },
        { name: 'GST Amendment', href: '/registration/gst-amendment' },
        { name: 'GST Cancellation', href: '/registration/gst-cancellation' },
        { name: 'GST Revocation', href: '/registration/gst-revocation' },
        { name: 'GST Return Filing', href: '/compliance-services?package=gst#compliance-builder' },
        { name: 'Annual Compliance', href: '/compliance-services' },
        { name: 'PF Registration', href: '/registration/pf-registration' },
        { name: 'ESI Registration', href: '/registration/esi-registration' },
        { name: 'Business Membership', href: '/business-membership' },
      ],
    },
  ];

  const social = {
    x: 'https://x.com/BizSarthi',
    instagram: 'https://www.instagram.com/bizsarthi.hq?utm_source=qr&igsh=MWtra2VmcGpqdHZyZQ==',
    linkedin: 'https://www.linkedin.com/in/bizsarthi-hq-a941383a8',
  };

  return (
    <footer className="bg-slate-900 text-slate-300 pb-20 lg:pb-0" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid gap-8 lg:grid-cols-[1.15fr_0.75fr_1.1fr]">
          <div className="rounded-3xl border border-white/10 bg-white/[0.035] p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-full bg-white ring-1 ring-white/10 flex items-center justify-center flex-shrink-0"><img src={companyBranding.logo} alt={companyBranding.name} className="w-full h-full object-contain object-center p-1 rounded-full animate-[spin_12s_linear_infinite]" /></div>
              <div>
                <h3 className="text-lg font-bold text-white font-display">{companyBranding.name}</h3>
                <p className="text-xs text-emerald-400 font-semibold">{companyBranding.tagline}</p>
              </div>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed mb-5">
              Business setup, GST, licences, business-address solutions and ongoing compliance support across India.
            </p>
            <div className="flex gap-3">
              <a href={social.x} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" title="X" aria-label="BizSarthi on X"><XIcon className="w-4 h-4" /></a>
              <a href={social.linkedin} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-blue-700 flex items-center justify-center transition-colors" title="LinkedIn" aria-label="BizSarthi on LinkedIn"><Linkedin className="w-4 h-4" /></a>
              <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-pink-600 flex items-center justify-center transition-colors" title="Instagram" aria-label="BizSarthi on Instagram"><Instagram className="w-4 h-4" /></a>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/[0.035] p-6">
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">Quick Links</h4>
            <ul className="space-y-2.5">
              {quickLinks.map((l) => (
                <li key={l.name}><a href={l.href} className="text-sm text-slate-400 hover:text-emerald-400 transition-colors">{l.name}</a></li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/[0.035] p-6">
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">Contact Us</h4>
            <div className="space-y-3 text-sm">
              <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"><Phone className="w-4 h-4 text-blue-400" /> {contactInfo.phone}</a>
              <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"><MessageCircle className="w-4 h-4 text-emerald-400" /> WhatsApp Us</a>
              <a href={`mailto:${contactInfo.email}`} className="flex min-w-0 items-center gap-2 text-slate-400 hover:text-white transition-colors"><Mail className="w-4 h-4 shrink-0 text-emerald-400" /> <span className="min-w-0 break-words">{contactInfo.email}</span></a>
              <a href={contactInfo.locationLink} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 text-slate-400 hover:text-white transition-colors"><MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" /> <span>{contactInfo.location}</span></a>
            </div>
          </div>
        </div>
        <div className="mt-8 rounded-[2rem] border border-white/10 bg-slate-950/45 p-6 sm:p-8">
          <div className="flex flex-col gap-3 border-b border-white/10 pb-5 sm:flex-row sm:items-center sm:justify-between">
            <div><p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-400">Complete service directory</p><h4 className="mt-2 font-display text-2xl font-semibold text-white">Choose the exact business support you need.</h4></div>
            <Link to="/services" className="inline-flex items-center text-sm font-semibold text-white transition-colors hover:text-emerald-300">Explore all services →</Link>
          </div>
          <div className="mt-6 grid gap-5 lg:grid-cols-3">
            {serviceGroups.map((group) => (
              <div key={group.title} className="min-w-0 rounded-2xl border border-white/10 bg-white/[0.035] p-5">
                <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-400">{group.title}</p>
                <ul className="grid gap-x-5 gap-y-3 sm:grid-cols-2">
                  {group.links.map((service) => (
                    <li key={service.name} className="min-w-0">
                      <Link to={service.href} className="block text-sm leading-5 text-slate-400 transition-colors hover:text-emerald-300">{service.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="mb-3 text-center text-xs font-semibold text-emerald-300">
            SetYourOffice — Business Address Solutions by BizSarthi
          </p>
          <p className="mb-4 text-center text-xs leading-relaxed text-slate-500">
            SetYourOffice is a business address solutions brand operated by BizSarthi Private Limited.
          </p>
          <p className="text-xs text-slate-500 leading-relaxed text-center">
            <strong className="text-slate-400">Disclaimer:</strong> BizSarthi is not a government website and is not affiliated with any government department. We provide professional assistance for registration, compliance, documentation, and filing services. Government approval depends on applicable laws, document correctness, and department verification.
          </p>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:flex md:items-center md:justify-between gap-4">
          <p className="text-xs text-slate-500 text-center md:text-left">
            © {currentYear} {companyBranding.legalName}. All rights reserved. CIN: {companyBranding.cin}
          </p>
          <div className="mt-3 md:mt-0 flex flex-wrap justify-center gap-x-5 gap-y-2">
            <Link to="/privacy-policy" className="text-xs text-slate-400 hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms-conditions" className="text-xs text-slate-400 hover:text-white transition-colors">Terms &amp; Conditions</Link>
            <Link to="/refund-policy" className="text-xs text-slate-400 hover:text-white transition-colors">Refund Policy</Link>
            <Link to="/disclaimer" className="text-xs text-slate-400 hover:text-white transition-colors">Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
